import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { isNotNullish } from '@bcef/ia-shared';
import { WA_LOCATION, WA_SESSION_STORAGE } from '@ng-web-apis/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { JsonConvert } from 'json2typescript';
import { catchError, filter, first, map, Observable, of, switchMap, tap } from 'rxjs';

import { FormStatus, RoutesPath } from '@features/succession/enums';
import { CustomerInformationResponse } from '@features/succession/interfaces';
import { CustomerInformation, ListBoxItems } from '@features/succession/models';
import { CustomerInformationService } from '@features/succession/services/customer-information.service';
import * as fromActions from '@features/succession/store/actions';
import * as fromSelectors from '@features/succession/store/selectors';
import { STORAGE_TOKEN_KEY } from '@shared/utils';

@Injectable()
export class CustomerInformationEffect {
  private readonly actions$: Actions = inject(Actions);

  private readonly customerInformationService: CustomerInformationService = inject(
    CustomerInformationService,
  );

  private readonly jsonConvert: JsonConvert = inject(JsonConvert);

  private readonly router: Router = inject(Router);

  private readonly store: Store = inject(Store);

  private readonly translate: TranslateService = inject(TranslateService);

  private readonly location: Location = inject(WA_LOCATION);

  private readonly storageService: Storage | null = inject(WA_SESSION_STORAGE);

  public initializeCustomerInformation$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromActions.CustomerInformationInitializing),
      switchMap(({ customerInformation }) =>
        this.customerInformationService.saveAndLoad(customerInformation).pipe(
          map((customerInformationResponse: CustomerInformationResponse) => {
            if (
              customerInformationResponse.formErrorDTO.isFormOK === FormStatus.KO_TOKEN ||
              customerInformationResponse.formErrorDTO.isFormOK === FormStatus.KO_WORKFLOW
            ) {
              return fromActions.CustomerInformationInitializingError({
                error: customerInformationResponse.formErrorDTO,
              });
            }
            return fromActions.CustomerInformationInitialized({
              customerInformation: this.jsonConvert.deserialize(
                customerInformationResponse.selfCareForm,
                CustomerInformation,
              ),
              selfCareListValues: this.jsonConvert.deserialize(
                customerInformationResponse.selfCareListValues,
                ListBoxItems,
              ),
            });
          }),
          catchError((error: HttpErrorResponse) =>
            of(fromActions.CustomerInformationInitializingError({ error })),
          ),
        ),
      ),
    );
  });

  public redirectAfterReloading$: Observable<string> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(fromActions.CustomerInformationInitialized),
        filter(() => this.router.routerState.snapshot.url === `/${RoutesPath.PAGE_ERROR}`),
        switchMap(() => this.store.select(fromSelectors.selectLoadPageUrl).pipe(isNotNullish())),
        tap((path) => this.router.navigate([path], { skipLocationChange: true })),
      );
    },
    { dispatch: false },
  );

  public loadCustomerInformation$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromActions.CustomerInformationSaveAndLoad),
      switchMap(({ customerInformation }) =>
        this.customerInformationService.saveAndLoad(customerInformation).pipe(
          map((customerInformationResponse: CustomerInformationResponse) => {
            if (
              customerInformationResponse.formErrorDTO.isFormOK === FormStatus.KO_TOKEN ||
              customerInformationResponse.formErrorDTO.isFormOK === FormStatus.KO_WORKFLOW
            ) {
              return fromActions.CustomerInformationSaveAndLoadError({
                error: customerInformationResponse.formErrorDTO,
              });
            }
            return fromActions.CustomerInformationSaveAndLoadComplete({
              customerInformationResponse,
            });
          }),
          catchError((error: HttpErrorResponse) =>
            of(fromActions.CustomerInformationSaveAndLoadError({ error })),
          ),
        ),
      ),
    );
  });

  public persistCustomerInformation$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromActions.CustomerInformationSaveAndLoadComplete),
      filter(({ customerInformationResponse }) => {
        return customerInformationResponse.formErrorDTO.isFormOK === FormStatus.OK;
      }),
      map(({ customerInformationResponse }) => {
        return fromActions.CustomerInformationPersist({
          customerInformation: this.jsonConvert.deserialize(
            customerInformationResponse.selfCareForm,
            CustomerInformation,
          ),
          selfCareListValues: this.jsonConvert.deserialize(
            customerInformationResponse.selfCareListValues,
            ListBoxItems,
          ),
        });
      }),
    );
  });

  public reloadLastRequest$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromActions.CustomerInformationReload),
      switchMap(() =>
        this.store
          .select(fromSelectors.selectLastCustomerInformationRequest)
          .pipe(isNotNullish(), first()),
      ),
      map((customerInformation: CustomerInformation) => {
        return fromActions.CustomerInformationInitializing({ customerInformation });
      }),
    );
  });

  public persistTokenToLocalStorage$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(fromActions.CustomerInformationSaveAndLoadComplete),
        filter(
          ({ customerInformationResponse }) => !!customerInformationResponse.selfCareForm?.token,
        ),
        tap(({ customerInformationResponse }) => {
          this.storageService?.setItem(
            STORAGE_TOKEN_KEY,
            customerInformationResponse.selfCareForm.token,
          );
        }),
      );
    },
    { dispatch: false },
  );

  public clearTokenFromLocalStorage$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(fromActions.CustomerInformationTokenClear),
        tap(() => {
          return this.storageService?.removeItem(STORAGE_TOKEN_KEY);
        }),
      );
    },
    { dispatch: false },
  );

  public redirectToDestinationStepAfterSaving$: Observable<string> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(fromActions.CustomerInformationPersist),
        switchMap(() => this.store.select(fromSelectors.selectLoadPageUrl).pipe(isNotNullish())),
        tap((path) => this.router.navigate([path], { skipLocationChange: true })),
      );
    },
    { dispatch: false },
  );

  public redirectToErrorPageWhenApiError$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          fromActions.CustomerInformationSaveAndLoadError,
          fromActions.CustomerInformationInitializingError,
        ),
        tap(() => this.router.navigate([RoutesPath.PAGE_ERROR], { skipLocationChange: true })),
      );
    },
    { dispatch: false },
  );

  public clearTokenWhenApiError$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        fromActions.CustomerInformationSaveAndLoadError,
        fromActions.CustomerInformationInitializingError,
      ),
      map(({ error }) => error),
      filter((error): error is CustomerInformationResponse['formErrorDTO'] => {
        return !!(error as CustomerInformationResponse['formErrorDTO']).isFormOK;
      }),
      filter((error) => {
        return error.isFormOK === FormStatus.KO_TOKEN || error.isFormOK === FormStatus.KO_WORKFLOW;
      }),
      map(() => fromActions.CustomerInformationTokenClear()),
    );
  });

  public clearTokenWhenConfirmation$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromActions.CustomerInformationConfirmation),
      map(() => fromActions.CustomerInformationTokenClear()),
    );
  });

  public clearTokenAfterLastStep$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromActions.CustomerInformationPersist),
      filter((information) => information.customerInformation.loadPage === '7'),
      map(() => fromActions.CustomerInformationTokenClear()),
    );
  });

  public redirectToStepOneWhenTokenCleared$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(fromActions.CustomerInformationResetWorkflow),
        tap(() => this.router.navigate([RoutesPath.PAGE_1], { skipLocationChange: true })),
      );
    },
    { dispatch: false },
  );

  public redirectAfterConfirmationPage$: Observable<string> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(fromActions.CustomerInformationConfirmation),
        switchMap(() => this.translate.get('screens.confirmation.button.link')),
        tap((link: string) => (this.location.href = link)),
      );
    },
    { dispatch: false },
  );
}
